import React from "react";
import { cash, shortDate } from "../../../services/api";
import Picture from "../../utils/Picture";

/**
 * Step 3 — what's included, and what can be added. Every offering, its
 * price and its places come from the admin for this date. Experiences
 * that need medical clearance are requested here and charged only once
 * the guest is cleared (spec 04) — the card says so before they choose.
 */
const StepAddons = ({ d, set, options, retreat }) => {
  const included = options.sessions.filter((s) => s.inclusion === "included");
  const extras = options.sessions.filter((s) => s.inclusion !== "included");
  const categories = [...new Set(extras.map((s) => s.category))];
  const limit = retreat.includedAddons;
  const pickedIncluded = included.filter((s) => d.addons[s.id] > 0).length;

  const toggle = (s) => set((cur) => ({ addons: { ...cur.addons, [s.id]: cur.addons[s.id] > 0 ? 0 : Math.min(cur.guests, s.placesLeft ?? cur.guests) } }));
  const qty = (s, n) => set((cur) => ({ addons: { ...cur.addons, [s.id]: n } }));

  if (!options.sessions.length) {
    return (
      <section className="bk-step" aria-labelledby="s3">
        <h2 id="s3" className="bk-step-title">Add-ons and experiences</h2>
        <p className="mk-muted">There's nothing to choose for these dates yet. Everything in the retreat is already included.</p>
      </section>
    );
  }

  return (
    <section className="bk-step" aria-labelledby="s3">
      <h2 id="s3" className="bk-step-title">Add-ons and experiences</h2>
      <p className="mk-muted">
        {retreat.decideLater ? "All optional. You can skip this step and tell us later." : "Choose what you'd like to join."}
      </p>

      {included.length > 0 && (
        <fieldset className="bk-group">
          <legend className="bk-group-name hFont">Included activities</legend>
          <p className="bk-muted">
            {limit > 0 ? `Your retreat includes ${limit} of these. ${pickedIncluded} chosen.` : "All included in your retreat price. Choose the ones you'd like to join."}
          </p>
          <div className="bk-addons">
            {included.map((s) => (
              <AddonCard key={s.id} s={s} on={d.addons[s.id] > 0} qty={d.addons[s.id] || 0} guests={d.guests}
                disabled={!(d.addons[s.id] > 0) && limit > 0 && pickedIncluded >= limit}
                onToggle={() => toggle(s)} onQty={(n) => qty(s, n)} />
            ))}
          </div>
        </fieldset>
      )}

      {categories.map((cat) => (
        <fieldset className="bk-group" key={cat}>
          <legend className="bk-group-name hFont">{cat}</legend>
          <div className="bk-addons">
            {extras.filter((s) => s.category === cat).map((s) => (
              <AddonCard key={s.id} s={s} on={d.addons[s.id] > 0} qty={d.addons[s.id] || 0} guests={d.guests}
                onToggle={() => toggle(s)} onQty={(n) => qty(s, n)} />
            ))}
          </div>
        </fieldset>
      ))}
    </section>
  );
};

const AddonCard = ({ s, on, qty, guests, disabled = false, onToggle, onQty }) => {
  const full = s.placesLeft === 0;
  const off = disabled || (full && !on);
  const when = [s.date ? shortDate(s.date) : null, s.time, s.duration].filter(Boolean).join(" · ");
  const price = s.inclusion === "included" ? "Included"
    : s.inclusion === "request" ? `${s.price > 0 ? cash(s.price) : "Price"} on request`
    : cash(s.price);

  return (
    <div className={`bk-addon ${on ? "is-on" : ""} ${off ? "is-off" : ""}`}>
      <label className="bk-addon-main">
        <input type="checkbox" checked={on} disabled={off} onChange={onToggle} />
        {s.image && <Picture src={s.image} sizes="96px" width={96} height={72} />}
        <span className="bk-addon-text">
          <span className="bk-addon-name">{s.name}</span>
          {when && <span className="bk-addon-when">{when}</span>}
          {s.summary && <span className="bk-addon-sum">{s.summary}</span>}
          {s.requiresClearance && <span className="bk-addon-flag">Needs medical clearance. Requested now, charged only once you're cleared.</span>}
          {s.inclusion === "request" && !s.requiresClearance && <span className="bk-addon-flag">On request. We'll confirm it with you.</span>}
        </span>
        <span className="bk-addon-price">
          {price}
          {s.inclusion === "paid" && s.listPrice > s.price && <s>{cash(s.listPrice)}</s>}
          {s.placesLeft != null && <small>{full ? "Full" : s.placesLeft <= 5 ? `${s.placesLeft} left` : ""}</small>}
        </span>
      </label>
      {on && guests > 1 && (
        <label className="bk-addon-qty">
          <span>For</span>
          <select value={qty} onChange={(e) => onQty(Number(e.target.value))}>
            {Array.from({ length: Math.min(guests, s.placesLeft == null ? guests : s.placesLeft) }, (_, i) => i + 1)
              .map((n) => <option key={n} value={n}>{n === guests ? `all ${guests} guests` : `${n} ${n === 1 ? "guest" : "guests"}`}</option>)}
          </select>
        </label>
      )}
    </div>
  );
};

export default StepAddons;
